import Helper from 'app/modules/Helper';
import RenderGA from 'app/components/Common/RenderGA';
import { DefaultSeo } from 'next-seo';
import type { AppProps } from 'next/app';
import { Montserrat } from 'next/font/google';
import { useSearchParams } from 'next/navigation';
import Script from 'next/script';
import { useEffect, useState } from 'react';
import { Provider, connect } from 'react-redux';
import { agreeCookieNotice, init, setLanguage } from '../actions/baseActions';
import '../app/locales/i18n';
import { useStore } from '../store';
import '../styles/app.scss';
import '../styles/langs.scss';
import '../styles/responsive.scss';

const montserrat = Montserrat({
	subsets: ['latin-ext', 'cyrillic-ext'],
	display: 'swap',
	weight: ['400', '500', '600', '700', '800'],
});

function App({ Component, pageProps, enabledVert, uid, imid }: AppProps & { enabledVert: string; uid?: string; imid?: string }) {
	const [hydrated, setHydrated] = useState(false);
	const [renderScript, setRenderScript] = useState(false);
	const [isSiteLoaded, setIsSiteLoaded] = useState(false);
	const [enableA11yScript, setEnableA11yScript] = useState(false);
	const store = useStore(pageProps.initialReduxState);
	const params = useSearchParams();
	const lng = Helper.getLanguageCode(params?.get('lng') as string);

	if (lng && typeof document !== 'undefined') {
		document.documentElement.lang = lng;

		// Set language in Redux store
		store.dispatch(setLanguage(lng));

		if (Helper.isRtlLanguage(lng)) {
			document.body.classList.add('rtl');
		}

		const langCode = Helper.getLanguageCode(lng);
		const isHebrew = langCode === 'he';
		if (isHebrew && !enableA11yScript) {
			setEnableA11yScript(true);
		}
	}

	useEffect(() => {
		let agreeCookieNoticeValue;
		try {
			agreeCookieNoticeValue = localStorage.getItem('cookieNotice');
			if (agreeCookieNoticeValue) {
				store.dispatch(agreeCookieNotice());
			}
		} catch (error) {
			console.error('Failed to read from localStorage, fallback to default value:', error);
		}
		setHydrated(true);
	}, []);

	useEffect(() => {
		if (typeof window === 'undefined') return;
		setRenderScript(!window.location.pathname.includes('/passthru'));

		// SHP-206: if the page is already loaded by the time this effect runs, the 'load'
		// event has fired and a listener would never run — so the GA/GTM scripts gated on
		// isSiteLoaded would never mount. Run the setup immediately in that case.
		const onLoad = () => setIsSiteLoaded(true);
		if (document.readyState === 'complete') {
			onLoad();
		} else {
			window.addEventListener('load', onLoad);
			return () => window.removeEventListener('load', onLoad);
		}
	}, []);

	if (!hydrated) {
		// Returns null on first render, so the client and server match
		return null;
	}

	return (
		<>
			<DefaultSeo
				openGraph={{
					type: 'website',
					locale: 'en_IE',
					url: process.env.NEXT_PUBLIC_SITE_URL,
					site_name: process.env.NEXT_PUBLIC_SITE_NAME,
				}}
			/>
			<RenderGA isSiteLoaded={isSiteLoaded} enabledVert={enabledVert} uid={uid} imid={imid} />
			{isSiteLoaded && (
				<>
					{enableA11yScript && (
						<Script strategy="afterInteractive" src="https://cdn.userway.org/widget.js" data-account="ckynQOSB8r" />
					)}
					{renderScript && (
						<Script
							strategy="lazyOnload"
							id="Cookiebot"
							src="https://consent.cookiebot.com/uc.js"
							data-cbid="ff633a12-41df-43d8-94fe-11fd95a12a11"
							type="text/javascript"
						/>
					)}
					{process.env.NEXT_PUBLIC_KLAVIYO_COMPANY_ID && (
						<Script
							id="klaviyo-script"
							strategy="afterInteractive"
							async
							{...{ fetchPriority: 'low' } as any}
							src={`//static.klaviyo.com/onsite/js/klaviyo.js?company_id=${process.env.NEXT_PUBLIC_KLAVIYO_COMPANY_ID}`}
						/>
					)}
					{process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID && (
						<Script
							id="microsoft-clarity"
							strategy="afterInteractive"
							dangerouslySetInnerHTML={{
								__html: `
									(function(c,l,a,r,i,t,y){
										c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
										t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
										y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
									})(window, document, "clarity", "script", "${process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID}");
								`,
							}}
						/>
					)}
				</>
			)}
			<main className={montserrat.className}>
				<Component {...pageProps} />
			</main>
		</>
	);
}

const mapStateToProps = (state: any) => ({
	enabledVert: state.base.enabledVert,
	uid: state.base.uid,
	imid: state.base.imid,
});

const ConnectedMyApp = connect(mapStateToProps, { setLanguage, agreeCookieNotice })(App);

function AppWithProvider({ Component, pageProps, router }: AppProps) {
	const store = useStore(pageProps.initialReduxState);
	// const { growthbook, callback: growthbookCallback } = useGrowthbook();
	// store.dispatch(setGrowthbookCallback(growthbookCallback));

	useEffect(() => {
		store.dispatch(init('hotels'));
	}, []);

	return (
		// <GrowthBookProvider growthbook={growthbook}>
		<Provider store={store}>
			<ConnectedMyApp Component={Component} pageProps={pageProps} router={router} />
		</Provider>
		// </GrowthBookProvider>
	);
}

export default AppWithProvider;
